import * as THREE from 'three';
import type { LegName } from '@/cat/quadrupedLocomotion';
import type { LegRigBinding, ProceduralRigBindings } from '@/cat/animationDriver';
import { findRigNode, validateAssetRig, type QuadrupedRigDef } from '@/engine/assetRig';

export interface ImportedRigResult {
  bindings: ProceduralRigBindings | null;
  problems: string[];
}

/** Resolves a validated manifest rig into the actor's animation boundary. */
export function bindImportedQuadrupedRig(
  root: THREE.Object3D,
  rig: QuadrupedRigDef,
): ImportedRigResult {
  const problems = validateAssetRig(root, rig);
  if (problems.length > 0) return { bindings: null, problems };

  const node = (name: string): THREE.Object3D => {
    const match = findRigNode(root, name);
    if (!match) throw new Error(`validated rig node "${name}" disappeared during binding`);
    return match;
  };
  const leg = (name: LegName): LegRigBinding => ({
    upper: node(rig.legs[name].upper),
    lower: node(rig.legs[name].lower),
    paw: node(rig.legs[name].paw),
  });

  return {
    bindings: {
      root,
      spine: node(rig.spine),
      head: node(rig.head),
      legs: {
        frontLeft: leg('frontLeft'),
        frontRight: leg('frontRight'),
        hindLeft: leg('hindLeft'),
        hindRight: leg('hindRight'),
      },
      tail: rig.tail.map(node),
      ears: [node(rig.ears[0]), node(rig.ears[1])],
    },
    problems: [],
  };
}
